import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { ColumnCard } from 'src/components/Card/ColumnCard';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { PROFILE_INSIGHTS } from 'src/components/Router';
import { useNavigateRoute } from 'src/components/Router/useNavigateRoute';
import { EDIT } from 'src/consts';
import { PROFILE_INSIGHTS_FORM } from 'src/context/FormContext';
import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';

import translations from './.vocab';
import { ActivitiesSection } from './ActivitiesSection';
import { ViewsSection } from './ViewsSection';
import { profileInsightsLoaded } from './actionCreators';
import { useGetProfileInsights } from './data';

export const ProfileInsights = () => {
  const dispatch = useDispatch();
  const { t } = useTranslations(translations);

  const { showProfileInsights, profileInsights, loading, error } =
    useGetProfileInsights();
  const { profileInsightsAnalyticsTracker } = useAnalytics();
  const navigate = useNavigateRoute();
  const showLoading = Boolean(loading || error);

  const toggleDrawer = () => {
    navigate({
      card: PROFILE_INSIGHTS,
      mode: EDIT,
    });
    if (showProfileInsights) {
      profileInsightsAnalyticsTracker({
        eventName: 'profile_insights_widget_pressed',
      });
    }
  };

  useEffect(() => {
    if (!showLoading && showProfileInsights) {
      dispatch(profileInsightsLoaded());
    }
  }, [
    showLoading,
    showProfileInsights,
    profileInsightsAnalyticsTracker,
    dispatch,
  ]);

  if (!showProfileInsights || !profileInsights) {
    return null;
  }

  const { viewCount, searchImpressionCount } = profileInsights || {};

  const cardButtonProps = {
    onClick: toggleDrawer,
    label: t('See activity'),
  };

  const loader = (
    <Stack space="large">
      <LoadingItem type="heading" level="1" />
      <LoadingItem type="text" size="standard" width="small" />
      <LoadingItem type="button" />
    </Stack>
  );

  return (
    <>
      <ColumnCard
        name={PROFILE_INSIGHTS}
        title={t('Profile activity')}
        loader={loader}
        loading={showLoading}
        buttonProps={cardButtonProps}
      >
        <Text>
          {searchImpressionCount > 0
            ? t('You appeared in n searches', { searchImpressionCount })
            : t('n profile views', { viewCount })}
        </Text>
      </ColumnCard>
      <FormDrawer
        title={t('Profile activity')}
        component={ProfileInsightsDrawerContent}
        card={PROFILE_INSIGHTS}
        formName={PROFILE_INSIGHTS_FORM}
      />
    </>
  );
};

const ProfileInsightsDrawerContent = () => {
  const { profileInsights } = useGetProfileInsights();
  const {
    viewCount = 0,
    searchImpressionCount = 0,
    activities = [],
  } = profileInsights || {};

  const { cardOpened } = useAnalytics();

  useEffect(() => {
    cardOpened({
      currentPage: 'profile talent search insights view',
      currentPageSection: 'talent search insights',
      currentPageSectionState: 'view',
    });
  }, [cardOpened]);

  return (
    <Stack space="xlarge">
      <ViewsSection
        viewCount={viewCount}
        searchImpressionCount={searchImpressionCount}
      />
      {activities.length > 0 ? (
        <ActivitiesSection activities={activities} />
      ) : null}
    </Stack>
  );
};
